import {
  Box,
  CircularProgress,
  Stack,
  SxProps,
  Theme,
  Typography
} from '@mui/material';
import { Checkbox } from 'common/form';
import { Accordion } from 'common/ui';
import { useUpdateConversationMutation } from 'generated-graphql/hooks/apollo-hooks';
import { NeedsReviewReason } from 'generated-graphql/types';
import { hasMoreTimeRequested } from 'modules/conversations/components/ConversationStatusIconAndLabel';
import { MoreTimeRequestedBadge } from 'modules/conversations/components/MoreTimeRequestedBadge';
import { ConversationItem } from 'modules/conversations/types/ConversationItem';
import { useState } from 'react';
import { ToastType, useToast } from 'services/toast';

type Props = {
  conversation: ConversationItem;
  onUpdateConversationLocally?: (conversation: ConversationItem) => void;
};

const warningBorderStyle: SxProps<Theme> = {
  borderTop: (theme) => theme.palette.border.small.warning.dark,
  borderBottom: (theme) => theme.palette.border.small.warning.dark
};

// MUI's CssBaseline resets `list-style: none` on ul, so the suggested-slot
// list draws its own bullet via ::before — same pattern as SummaryField.
const timeSlotBulletSx: SxProps = {
  fontSize: '14px',
  position: 'relative',
  pl: 1.5,
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '0.6em',
    width: 4,
    height: 4,
    backgroundColor: 'currentColor',
    borderRadius: '50%'
  }
};

const checkboxSx: SxProps = {
  '& .MuiCheckbox-root': {
    transform: 'scale(0.64)',
    transformOrigin: 'left center'
  },
  '& .MuiTypography-root': {
    fontSize: '16px',
    fontWeight: 600,
    marginLeft: '4px'
  }
};

const hasEverHadMoreTimeRequested = (conversation: ConversationItem): boolean =>
  (conversation.needsReviewItems?.items ?? []).some(
    (item) => item?.reason === NeedsReviewReason.MoreTimeRequested
  );

// Same-day → "HH:mm - HH:mm tz, EEEE d MMM yyyy".
// Cross-day → "HH:mm EEE d MMM - HH:mm EEE d MMM yyyy tz" — the candidate
// web app can submit windows spanning multiple days, so we widen the
// format rather than misrepresent them as same-day reversed times.
const formatSuggestedSlot = (
  start: string,
  end: string,
  timezone?: string | null
) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const buildFormatters = (tz: string | undefined) => ({
    time: new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz
    }),
    dateLong: new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: tz
    }),
    dateShort: new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: tz
    }),
    year: new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      timeZone: tz
    }),
    dayKey: new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: tz
    })
  });

  const formatters = ((): ReturnType<typeof buildFormatters> => {
    try {
      return buildFormatters(timezone ?? undefined);
    } catch {
      return buildFormatters(undefined);
    }
  })();

  const startTime = formatters.time.format(startDate);
  const endTime = formatters.time.format(endDate);
  const tzSuffix = timezone ? ` ${timezone}` : '';

  const sameDay =
    formatters.dayKey.format(startDate) === formatters.dayKey.format(endDate);

  if (sameDay) {
    return `${startTime} - ${endTime}${tzSuffix}, ${formatters.dateLong.format(
      startDate
    )}`;
  }

  return `${startTime} ${formatters.dateShort.format(
    startDate
  )} - ${endTime} ${formatters.dateShort.format(
    endDate
  )} ${formatters.year.format(endDate)}${tzSuffix}`;
};

const getLatestSuggestion = (conversation: ConversationItem) => {
  const suggestions = (
    conversation.candidateAvailabilitySuggestions?.items ?? []
  ).filter((s): s is NonNullable<typeof s> => Boolean(s));
  if (!suggestions.length) return null;
  return [...suggestions].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt)
  )[0];
};

export const MoreTimeRequestedAccordion: React.FC<Props> = ({
  conversation,
  onUpdateConversationLocally
}) => {
  const [updateConversation, { loading }] = useUpdateConversationMutation();
  const { showToast } = useToast();
  const [resolvedConversationId, setResolvedConversationId] = useState<
    string | null
  >(null);

  const isUnresolved = hasMoreTimeRequested(
    conversation.unresolvedNeedsReviewReasons
  );
  const everHad = hasEverHadMoreTimeRequested(conversation);

  if (conversation.calendarBookingId || (!isUnresolved && !everHad)) {
    return null;
  }

  const locallyResolved = resolvedConversationId === conversation.id;
  const isResolved = locallyResolved || !isUnresolved;
  const latestSuggestion = getLatestSuggestion(conversation);

  const handleResolve = async (isChecked: boolean) => {
    if (!isChecked || isResolved || loading) return;

    const existing = Array.isArray(conversation.unresolvedNeedsReviewReasons)
      ? conversation.unresolvedNeedsReviewReasons
      : [];
    const updated = existing.filter(
      (reason) => reason !== NeedsReviewReason.MoreTimeRequested
    );

    try {
      const nextNeedsReview = updated.length > 0;
      await updateConversation({
        variables: {
          input: {
            id: conversation.id,
            unresolvedNeedsReviewReasons: updated,
            needsReview: nextNeedsReview
          }
        }
      });
      setResolvedConversationId(conversation.id);
      onUpdateConversationLocally?.({
        ...conversation,
        unresolvedNeedsReviewReasons: updated,
        needsReview: nextNeedsReview
      });
      showToast({
        title: 'Marked as resolved',
        type: ToastType.Success
      });
    } catch {
      showToast({
        title: 'Could not resolve',
        message: 'Please try again.',
        type: ToastType.Error
      });
    }
  };

  const header = (
    <>
      <Typography component="span" noWrap>
        Meeting
      </Typography>
      <Box flex={1} />
      {!isResolved && <MoreTimeRequestedBadge />}
    </>
  );

  return (
    <Box sx={isResolved ? undefined : warningBorderStyle}>
      <Accordion
        title={header}
        backgroundColor={isResolved ? 'customGrey.light' : 'warningV2.light'}
      >
        <Stack spacing={2}>
          <Typography variant="body2">
            The candidate requested additional meeting slots to be provided.
          </Typography>
          {latestSuggestion && latestSuggestion.timeSlots.length > 0 && (
            <Stack spacing={1}>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {latestSuggestion.timeSlots.map((slot, index) => (
                  <Typography
                    key={`${slot.start}-${index}`}
                    component="li"
                    sx={timeSlotBulletSx}
                  >
                    {formatSuggestedSlot(
                      slot.start,
                      slot.end,
                      latestSuggestion.timezone
                    )}
                  </Typography>
                ))}
              </Box>
              {latestSuggestion.notes && (
                <Typography sx={{ fontSize: '14px', fontStyle: 'italic' }}>
                  &ldquo;{latestSuggestion.notes}&rdquo;
                </Typography>
              )}
            </Stack>
          )}
          <Typography variant="body2">
            Takeover the conversation and arrange a different time to meet. Hand
            back when you&apos;re done.
          </Typography>
          <Typography variant="body2">
            Any meetings not booked through Popp will need to be managed
            separately.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              value={isResolved}
              onChange={handleResolve}
              field={{
                checkboxLabel: 'Resolved',
                controlSx: checkboxSx,
                disabled: loading
              }}
            />
            {loading && <CircularProgress size={14} />}
          </Stack>
        </Stack>
      </Accordion>
    </Box>
  );
};
